// 访客 SDK 内置多语种文案。
// 设计原则：
//   - 无第三方依赖，纯字典查表 + 占位符替换
//   - 仅 5 种内置：zh-CN / zh-TW / en / id / ms
//   - 默认按 navigator.language 自动选择，可由 InitOptions.locale 强制覆盖
//   - 任何缺失键回退到英文，避免出现键名穿透

export type Locale = 'zh-CN' | 'zh-TW' | 'en' | 'id' | 'ms';

export const SUPPORTED_LOCALES: Locale[] = ['zh-CN', 'zh-TW', 'en', 'id', 'ms'];

// 所有面向访客的字符串键。新增字符串只能加在这里。
export interface Messages {
  title: string;
  statusConnecting: string;
  statusOnline: string;
  statusDisconnected: string;
  statusReconnecting: (attempt: number, delayMs: number) => string;
  launcherAriaLabel: string;
  closeAriaLabel: string;
  attachAriaLabel: string;
  emojiAriaLabel: string;
  sendButton: string;
  textareaPlaceholder: string;
  msgStateSending: string;
  msgStateSent: string;
  msgStateRead: string;
  msgStateFailed: string;
  uploading: (filename: string) => string;
  fileTooLarge: (limitMB: number) => string;
  sessionNotReady: string;
  noAgentOnline: string;
  queueBar: (position: number, total: number) => string;
  conversationCreateFailed: (detail: string) => string;
  uploadFailed: (detail: string) => string;
  sendFailed: (detail: string) => string;
  ackFailed: (detail: string) => string;
  genericError: (detail: string) => string;
  msgRecalled: string;
  endConversation: string;
  endConfirm: string;
  endedNotice: string;
  /** 客服主动结束会话时的轻量提示（输入框保持可用，继续打字自动重连） */
  endedByAgentNotice: string;
  restartConversation: string;
  senderAgent: string;
  senderBot: string;
  senderSystem: string;
  /** 引用条/气泡里没有 senderName 时的「我」回退（self 端） */
  senderSelf: string;
  /** 桌面通知与引用预览共用的媒体类型占位 */
  mediaImage: string;
  mediaVideo: string;
  mediaFile: string;
  /** 输入框上方引用条左侧标签（"引用 "/"Reply to " 等） */
  quoteBarLabel: string;
  /** 引用条右侧 × 按钮的无障碍标签 */
  quoteBarClearAriaLabel: string;
  /** 气泡浮出的「引用」按钮显示文字与悬浮提示 */
  quoteButtonLabel: string;
  quoteButtonTitle: string;
  idleTimeoutNotice: string;
  idleWarningNotice: (discMinutes: number) => string;
  queueDisconnectedNotice: string;
  queueTimeoutNotice: string;
  transferredNotice: (agentName: string) => string;
  historyDivider: string;
  historyView: string;
  historyMore: string;
  historyLoading: string;
  historyEmpty: string;
  dateLabel: (d: Date) => string;
  ratingTitle: string;
  ratingPrompt: string;
  ratingCommentPlaceholder: string;
  ratingSubmit: string;
  ratingSkip: string;
  ratingThanks: string;
  ratingSubmitFailed: (detail: string) => string;
}

const enMessages: Messages = {
  title: 'Customer Support',
  statusConnecting: 'Connecting…',
  statusOnline: 'Online',
  statusDisconnected: 'Network lost · messages may not be delivered',
  statusReconnecting: (a, d) => `Reconnecting… (#${a}, ${d}ms)`,
  launcherAriaLabel: 'Open chat',
  closeAriaLabel: 'Close',
  attachAriaLabel: 'Upload file',
  emojiAriaLabel: 'Insert emoji',
  sendButton: 'Send',
  textareaPlaceholder: 'Type a message…',
  msgStateSending: 'Sending…',
  msgStateSent: 'Delivered',
  msgStateRead: 'Read',
  msgStateFailed: 'Failed',
  uploading: (n) => `Uploading: ${n}`,
  fileTooLarge: (mb) => `File exceeds ${mb} MB`,
  sessionNotReady: 'Session is not ready, please wait…',
  noAgentOnline: 'All agents are busy, please wait…',
  queueBar: (pos, total) => `You are #${pos} in queue (${total} waiting). An agent will join soon.`,
  conversationCreateFailed: (d) => `Failed to start conversation: ${d}`,
  uploadFailed: (d) => `Upload failed: ${d}`,
  sendFailed: (d) => `Send failed: ${d}`,
  ackFailed: (d) => `Send failed: ${d}`,
  genericError: (d) => `Error: ${d}`,
  msgRecalled: '[Message recalled]',
  endConversation: 'End chat',
  endConfirm: 'End this chat? You will not be able to send more messages.',
  endedNotice: 'Chat has ended.',
  endedByAgentNotice: 'The agent has ended the chat — keep typing and we’ll reconnect you.',
  restartConversation: 'Start a new chat',
  senderAgent: 'Agent',
  senderBot: 'Bot',
  senderSystem: 'System',
  senderSelf: 'You',
  mediaImage: '[Image]',
  mediaVideo: '[Video]',
  mediaFile: '[File]',
  quoteBarLabel: 'Reply to ',
  quoteBarClearAriaLabel: 'Cancel reply',
  quoteButtonLabel: 'Reply',
  quoteButtonTitle: 'Reply to this message',
  idleTimeoutNotice: 'Chat closed due to inactivity.',
  idleWarningNotice: (d) =>
    `You haven't replied for a while. If there is no reply in ${d} minutes the chat will end automatically.`,
  queueDisconnectedNotice:
    'Connection lost. Please refresh the page to rejoin the queue.',
  queueTimeoutNotice:
    'All agents are busy and no one is available right now. Please try again later.',
  transferredNotice: (name) => `This chat has been transferred to ${name}.`,
  historyDivider: 'Previous conversation',
  historyView: 'View previous chats',
  historyMore: 'Load older',
  historyLoading: 'Loading…',
  historyEmpty: 'No previous chats',
  dateLabel: (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
  ratingTitle: 'Rate this chat',
  ratingPrompt: 'How was your experience?',
  ratingCommentPlaceholder: 'Leave a comment (optional)',
  ratingSubmit: 'Submit',
  ratingSkip: 'Skip',
  ratingThanks: 'Thanks for your feedback!',
  ratingSubmitFailed: (d) => `Submit failed: ${d}`,
};

const zhCNMessages: Messages = {
  title: '在线客服',
  statusConnecting: '连接中…',
  statusOnline: '在线',
  statusDisconnected: '网络已断开，消息可能无法送达',
  statusReconnecting: (a, d) => `重连中… (#${a}, ${d}ms)`,
  launcherAriaLabel: '打开客服',
  closeAriaLabel: '关闭',
  attachAriaLabel: '上传文件',
  emojiAriaLabel: '插入表情',
  sendButton: '发送',
  textareaPlaceholder: '说点什么…',
  msgStateSending: '发送中…',
  msgStateSent: '已送达',
  msgStateRead: '已读',
  msgStateFailed: '失败',
  uploading: (n) => `上传中: ${n}`,
  fileTooLarge: (mb) => `文件超过 ${mb} MB`,
  sessionNotReady: '会话尚未就绪，请稍候…',
  noAgentOnline: '当前坐席繁忙，请稍候…',
  queueBar: (pos, total) => `您前面还有 ${pos - 1} 位访客（共 ${total} 位排队），客服马上为您服务`,
  conversationCreateFailed: (d) => `会话创建失败：${d}`,
  uploadFailed: (d) => `上传失败：${d}`,
  sendFailed: (d) => `发送失败：${d}`,
  ackFailed: (d) => `发送失败：${d}`,
  genericError: (d) => `错误：${d}`,
  msgRecalled: '[消息已撤回]',
  endConversation: '结束会话',
  endConfirm: '确定结束本次会话？结束后将无法继续发送消息。',
  endedNotice: '会话已结束。',
  endedByAgentNotice: '坐席已结束对话，你可以继续回复消息以重新连线。',
  restartConversation: '重新开始对话',
  senderAgent: '客服',
  senderBot: '机器人',
  senderSystem: '系统',
  senderSelf: '我',
  mediaImage: '[图片]',
  mediaVideo: '[视频]',
  mediaFile: '[文件]',
  quoteBarLabel: '引用 ',
  quoteBarClearAriaLabel: '取消引用',
  quoteButtonLabel: '引用',
  quoteButtonTitle: '引用此消息',
  idleTimeoutNotice: '因长时间未回复，会话已自动结束。',
  idleWarningNotice: (d) => `您已长时间未回复消息，若${d}分钟内仍无回复，系统将自动结束本次会话。`,
  queueDisconnectedNotice: '连接已断开，请刷新页面重新排队。',
  queueTimeoutNotice: '坐席繁忙，当前暂无人接入，请稍后重试。',
  transferredNotice: (name) => `当前对话已转接给新的客服 ${name}。`,
  historyDivider: '以上为历史消息',
  historyView: '查看历史对话',
  historyMore: '加载更多历史',
  historyLoading: '加载中…',
  historyEmpty: '暂无历史对话',
  dateLabel: (d) => `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`,
  ratingTitle: '会话评价',
  ratingPrompt: '本次服务怎么样？',
  ratingCommentPlaceholder: '留下您的评价（可选）',
  ratingSubmit: '提交',
  ratingSkip: '跳过',
  ratingThanks: '感谢您的反馈！',
  ratingSubmitFailed: (d) => `提交失败：${d}`,
};

const zhTWMessages: Messages = {
  title: '線上客服',
  statusConnecting: '連線中…',
  statusOnline: '線上',
  statusDisconnected: '網路已斷線，訊息可能無法送達',
  statusReconnecting: (a, d) => `重新連線中… (#${a}, ${d}ms)`,
  launcherAriaLabel: '開啟客服',
  closeAriaLabel: '關閉',
  attachAriaLabel: '上傳檔案',
  emojiAriaLabel: '插入表情',
  sendButton: '傳送',
  textareaPlaceholder: '輸入訊息…',
  msgStateSending: '傳送中…',
  msgStateSent: '已送達',
  msgStateRead: '已讀',
  msgStateFailed: '失敗',
  uploading: (n) => `上傳中: ${n}`,
  fileTooLarge: (mb) => `檔案超過 ${mb} MB`,
  sessionNotReady: '會話尚未就緒，請稍候…',
  noAgentOnline: '目前客服忙線中，請稍候…',
  queueBar: (pos, total) => `您前面還有 ${pos - 1} 位訪客（共 ${total} 位排隊中），客服馬上為您服務`,
  conversationCreateFailed: (d) => `建立會話失敗：${d}`,
  uploadFailed: (d) => `上傳失敗：${d}`,
  sendFailed: (d) => `傳送失敗：${d}`,
  ackFailed: (d) => `傳送失敗：${d}`,
  genericError: (d) => `錯誤：${d}`,
  msgRecalled: '[訊息已撤回]',
  endConversation: '結束會話',
  endConfirm: '確定結束本次會話？結束後將無法繼續傳送訊息。',
  endedNotice: '會話已結束。',
  endedByAgentNotice: '客服已結束對話，你可以繼續回覆訊息以重新連線。',
  restartConversation: '重新開始對話',
  senderAgent: '客服',
  senderBot: '機器人',
  senderSystem: '系統',
  senderSelf: '我',
  mediaImage: '[圖片]',
  mediaVideo: '[影片]',
  mediaFile: '[檔案]',
  quoteBarLabel: '引用 ',
  quoteBarClearAriaLabel: '取消引用',
  quoteButtonLabel: '引用',
  quoteButtonTitle: '引用此訊息',
  idleTimeoutNotice: '因長時間未回覆，會話已自動結束。',
  idleWarningNotice: (d) => `您已長時間未回覆訊息，若${d}分鐘內仍無回覆，系統將自動結束本次會話。`,
  queueDisconnectedNotice: '連線已中斷，請重新整理頁面重新排隊。',
  queueTimeoutNotice: '客服忙線中，目前暫無人接入，請稍後重試。',
  transferredNotice: (name) => `目前對話已轉接給新的客服 ${name}。`,
  historyDivider: '以上為歷史訊息',
  historyView: '查看歷史對話',
  historyMore: '載入更多歷史',
  historyLoading: '載入中…',
  historyEmpty: '暫無歷史對話',
  dateLabel: (d) => `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`,
  ratingTitle: '會話評價',
  ratingPrompt: '本次服務如何？',
  ratingCommentPlaceholder: '留下您的評價（選填）',
  ratingSubmit: '送出',
  ratingSkip: '跳過',
  ratingThanks: '感謝您的回饋！',
  ratingSubmitFailed: (d) => `送出失敗：${d}`,
};

const idMessages: Messages = {
  title: 'Layanan Pelanggan',
  statusConnecting: 'Menghubungkan…',
  statusOnline: 'Online',
  statusDisconnected: 'Terputus',
  statusReconnecting: (a, d) => `Menghubungkan ulang… (#${a}, ${d}ms)`,
  launcherAriaLabel: 'Buka obrolan',
  closeAriaLabel: 'Tutup',
  attachAriaLabel: 'Unggah berkas',
  emojiAriaLabel: 'Sisipkan emoji',
  sendButton: 'Kirim',
  textareaPlaceholder: 'Tulis pesan…',
  msgStateSending: 'Mengirim…',
  msgStateSent: 'Terkirim',
  msgStateRead: 'Dibaca',
  msgStateFailed: 'Gagal',
  uploading: (n) => `Mengunggah: ${n}`,
  fileTooLarge: (mb) => `Berkas melebihi ${mb} MB`,
  sessionNotReady: 'Sesi belum siap, mohon tunggu…',
  noAgentOnline: 'Semua agen sedang sibuk, mohon tunggu…',
  queueBar: (pos, total) => `Antrean Anda: #${pos} dari ${total}. Agen akan segera melayani Anda.`,
  conversationCreateFailed: (d) => `Gagal memulai percakapan: ${d}`,
  uploadFailed: (d) => `Unggah gagal: ${d}`,
  sendFailed: (d) => `Pengiriman gagal: ${d}`,
  ackFailed: (d) => `Pengiriman gagal: ${d}`,
  genericError: (d) => `Kesalahan: ${d}`,
  msgRecalled: '[Pesan ditarik kembali]',
  endConversation: 'Akhiri obrolan',
  endConfirm: 'Akhiri obrolan ini? Anda tidak dapat mengirim pesan lagi.',
  endedNotice: 'Obrolan telah berakhir.',
  endedByAgentNotice: 'Agen telah menutup obrolan — tulis pesan lagi untuk menyambungkan kembali.',
  restartConversation: 'Mulai obrolan baru',
  senderAgent: 'Agen',
  senderBot: 'Bot',
  senderSystem: 'Sistem',
  senderSelf: 'Anda',
  mediaImage: '[Gambar]',
  mediaVideo: '[Video]',
  mediaFile: '[Berkas]',
  quoteBarLabel: 'Balas ',
  quoteBarClearAriaLabel: 'Batalkan balasan',
  quoteButtonLabel: 'Balas',
  quoteButtonTitle: 'Balas pesan ini',
  idleTimeoutNotice: 'Obrolan berakhir karena tidak ada balasan.',
  idleWarningNotice: (d) =>
    `Anda belum membalas pesan untuk sementara waktu. Jika tidak ada balasan dalam ${d} menit, obrolan akan ditutup otomatis.`,
  queueDisconnectedNotice:
    'Koneksi terputus. Silakan muat ulang halaman untuk masuk antrean kembali.',
  queueTimeoutNotice:
    'Semua agen sedang sibuk dan belum ada yang tersedia saat ini. Silakan coba lagi nanti.',
  transferredNotice: (name) => `Obrolan ini telah dialihkan ke ${name}.`,
  historyDivider: 'Riwayat percakapan sebelumnya',
  historyView: 'Lihat percakapan sebelumnya',
  historyMore: 'Muat lebih lama',
  historyLoading: 'Memuat…',
  historyEmpty: 'Tidak ada percakapan sebelumnya',
  dateLabel: (d) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`,
  ratingTitle: 'Nilai obrolan ini',
  ratingPrompt: 'Bagaimana pengalaman Anda?',
  ratingCommentPlaceholder: 'Tinggalkan komentar (opsional)',
  ratingSubmit: 'Kirim',
  ratingSkip: 'Lewati',
  ratingThanks: 'Terima kasih atas masukan Anda!',
  ratingSubmitFailed: (d) => `Pengiriman gagal: ${d}`,
};

const msMessages: Messages = {
  title: 'Khidmat Pelanggan',
  statusConnecting: 'Menyambung…',
  statusOnline: 'Dalam talian',
  statusDisconnected: 'Terputus',
  statusReconnecting: (a, d) => `Menyambung semula… (#${a}, ${d}ms)`,
  launcherAriaLabel: 'Buka sembang',
  closeAriaLabel: 'Tutup',
  attachAriaLabel: 'Muat naik fail',
  emojiAriaLabel: 'Sisipkan emoji',
  sendButton: 'Hantar',
  textareaPlaceholder: 'Taip mesej…',
  msgStateSending: 'Menghantar…',
  msgStateSent: 'Dihantar',
  msgStateRead: 'Dibaca',
  msgStateFailed: 'Gagal',
  uploading: (n) => `Memuat naik: ${n}`,
  fileTooLarge: (mb) => `Fail melebihi ${mb} MB`,
  sessionNotReady: 'Sesi belum sedia, sila tunggu…',
  noAgentOnline: 'Semua ejen sedang sibuk, sila tunggu…',
  queueBar: (pos, total) => `Anda berada di #${pos} dalam baris (${total} menunggu). Ejen akan segera tiba.`,
  conversationCreateFailed: (d) => `Gagal memulakan perbualan: ${d}`,
  uploadFailed: (d) => `Muat naik gagal: ${d}`,
  sendFailed: (d) => `Hantar gagal: ${d}`,
  ackFailed: (d) => `Hantar gagal: ${d}`,
  genericError: (d) => `Ralat: ${d}`,
  msgRecalled: '[Mesej telah ditarik balik]',
  endConversation: 'Tamatkan sembang',
  endConfirm: 'Tamatkan sembang ini? Anda tidak akan dapat menghantar mesej lagi.',
  endedNotice: 'Sembang telah tamat.',
  endedByAgentNotice: 'Ejen telah menamatkan sembang — taip mesej lagi untuk menyambung semula.',
  restartConversation: 'Mulakan sembang baharu',
  senderAgent: 'Ejen',
  senderBot: 'Bot',
  senderSystem: 'Sistem',
  senderSelf: 'Anda',
  mediaImage: '[Gambar]',
  mediaVideo: '[Video]',
  mediaFile: '[Fail]',
  quoteBarLabel: 'Balas ',
  quoteBarClearAriaLabel: 'Batalkan balasan',
  quoteButtonLabel: 'Balas',
  quoteButtonTitle: 'Balas mesej ini',
  idleTimeoutNotice: 'Sembang ditutup kerana tiada balasan.',
  idleWarningNotice: (d) =>
    `Anda belum membalas mesej buat sementara waktu. Jika tiada balasan dalam ${d} minit, sembang akan ditutup secara automatik.`,
  queueDisconnectedNotice:
    'Sambungan terputus. Sila muat semula halaman untuk menyertai baris semula.',
  queueTimeoutNotice:
    'Semua ejen sibuk dan tiada siapa tersedia buat masa ini. Sila cuba lagi kemudian.',
  transferredNotice: (name) => `Sembang ini telah dipindahkan kepada ${name}.`,
  historyDivider: 'Sembang sebelumnya',
  historyView: 'Lihat sembang sebelumnya',
  historyMore: 'Muat lebih lama',
  historyLoading: 'Memuatkan…',
  historyEmpty: 'Tiada sembang sebelumnya',
  dateLabel: (d) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`,
  ratingTitle: 'Nilai sembang ini',
  ratingPrompt: 'Bagaimana pengalaman anda?',
  ratingCommentPlaceholder: 'Tinggalkan komen (pilihan)',
  ratingSubmit: 'Hantar',
  ratingSkip: 'Langkau',
  ratingThanks: 'Terima kasih atas maklum balas anda!',
  ratingSubmitFailed: (d) => `Hantar gagal: ${d}`,
};

const dict: Record<Locale, Messages> = {
  'zh-CN': zhCNMessages,
  'zh-TW': zhTWMessages,
  en: enMessages,
  id: idMessages,
  ms: msMessages,
};

// resolveLocale 把任意 BCP-47 标签或显式 Locale 归一为内置 Locale。
//   - 显式参数优先：若已是支持的 Locale，原样返回
//   - 中文区域：Hant / TW / HK / MO → zh-TW；其它 zh-* → zh-CN
//   - id / ms：直接命中前缀
//   - 其它：兜底 en
export function resolveLocale(input?: string | null): Locale {
  if (input) {
    const normalized = normalize(input);
    if (normalized) return normalized;
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const fromNav = normalize(navigator.language);
    if (fromNav) return fromNav;
  }
  return 'en';
}

function normalize(tag: string): Locale | null {
  const lower = tag.toLowerCase();
  if (lower.startsWith('zh')) {
    if (
      lower.includes('hant') ||
      lower.includes('tw') ||
      lower.includes('hk') ||
      lower.includes('mo')
    ) {
      return 'zh-TW';
    }
    return 'zh-CN';
  }
  if (lower.startsWith('id') || lower.startsWith('in')) return 'id'; // 兼容 "in" 旧标签
  if (lower.startsWith('ms')) return 'ms';
  if (lower.startsWith('en')) return 'en';
  return null;
}

// getMessages 返回指定 locale 的整张文案表（保证非空）。
export function getMessages(locale: Locale): Messages {
  return dict[locale] ?? enMessages;
}
